import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { absoluteUrl, getPageSeo } from '../seo.mjs';
import { trackPageView } from '../analytics';

/** Adds the tag if the built HTML did not already ship one, then updates it. */
function setMeta(attribute, name, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

/**
 * Keeps the document title in sync and restores the browser's native
 * navigation behaviour that a single page router otherwise loses: new routes
 * start at the top, and in-page anchors scroll to their target.
 *
 * Also carries the per-route metadata. The built HTML only holds the landing
 * page's tags, because one shell is served for every route, so each navigation
 * has to rewrite them for anything that renders the page before reading them.
 */
export default function RouteEffects() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const { title, description, robots } = getPageSeo(pathname);
    // Deliberately drops the query string: the game opens /feedback with
    // ?source= and ?version= set, and every variant is the same page.
    const canonical = absoluteUrl(pathname);

    document.title = title;
    setCanonical(canonical);
    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    // Sent after the title is set so the hit is attributed to the right page.
    trackPageView(pathname, title);
  }, [pathname]);

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    // The target section may mount in the same frame as the navigation.
    const frame = window.requestAnimationFrame(() => {
      document.querySelector(hash)?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}
